// Per-SKU demand forecast for the SKU Family Live tool, split by channel:
// D2C web store (d2c), POS / convention booths (pos) and B2B — distributor +
// wholesale, DIST/WHSL (b2b). Each
// channel has its own demand today and its own growth rate; the projection
// adds them day by day, so a SKU whose web sales are growing while
// distributors cut back runs out on the combined curve. Shared by the reorder
// plan and the runout charts. Pure functions, no React. The rates themselves
// are computed server-side (sku_family_growth.py, yoy.forecast).
import { growthMultiplier } from './skuFamilyLiveEngine';

export const CHANNELS = [
  { key: 'd2c', server: 'd2c', label: 'D2C' },
  { key: 'pos', server: 'pos', label: 'POS' },
  { key: 'b2b', server: 'wholesale', label: 'B2B' },
];

const DAYS_PER_MONTH = 30.44;
const MIN_IN_STOCK_DAYS = 60;      // below this, sales/in-stock-days is too thin

// Total units/day from the 180-day sell rate, for SKUs with no 12-month
// channel rates (launched or back in stock too recently). Returns {rate, basis}.
function fallbackRate(s, skuEntry) {
  const raw = s.rate || 0;
  const inStock = s.in_stock_days_180;
  if (inStock != null && s.history_days_180 >= 150) {
    if (inStock >= MIN_IN_STOCK_DAYS) {
      return inStock < s.history_days_180
        ? { rate: raw * s.history_days_180 / inStock, basis: `180-day rate, OOS-adjusted (${inStock}/${s.history_days_180} days in stock)` }
        : { rate: raw, basis: '180-day rate' };
    }
    // Mostly out of stock lately: fall back to its in-stock months' average.
    const w = skuEntry?.window_12mo;
    if (w) {
      const skipped = new Set(w.excluded_months || []);
      const units = w.actual_units.filter((_, i) => !skipped.has(w.months[i]));
      if (units.length >= 3) {
        const avg = units.reduce((a, b) => a + b, 0) / units.length;
        return { rate: avg / DAYS_PER_MONTH, basis: `in-stock months avg (only ${inStock} days in stock)` };
      }
    }
    return { rate: raw, basis: `180-day rate — only ${inStock} days in stock, likely understated` };
  }
  return { rate: raw, basis: '180-day rate (no stock history)' };
}

// Forecast for one SKU: { d2c: {today, rate, source, sourceKey},
// pos: {...}, b2b: {...}, today (total units/day now), rate (effective 1-year change in
// total demand), basis }.
//
// Demand today per channel = the SKU's last-12-months daily rate in that
// channel (days in stock only) brought forward to today by that channel's
// growth (yoy.forecast). In a market view both channels are scaled by the
// market's share of the SKU's last-180-day sales. Without 12-month rates, the
// 180-day fallback total is split by the SKU's (else its family's) channel
// mix this year, each part growing at that channel's rate.
export function skuForecast(s, skuEntry, familyEntry) {
  const own = skuEntry?.yoy?.forecast;
  const fam = familyEntry?.yoy?.forecast;
  const rateOf = c => own?.channels[c.server] || fam?.channels[c.server]
    || { rate: 0, source: 'default', source_key: null };
  const out = {};
  let basis;

  const share = s.market_share;
  if (own?.today_per_day != null && share !== null) {
    const scale = share === undefined ? 1 : share;
    for (const c of CHANNELS) {
      const ch = own.channels[c.server];
      out[c.key] = { today: ch.today_per_day * scale, rate: ch.rate, source: ch.source, sourceKey: ch.source_key };
    }
    basis = `12-month daily rate per channel (${skuEntry.yoy.this_year_days} days in stock), brought to today`
      + (share === undefined ? '' : ` × ${Math.round(share * 100)}% market share (180 days)`);
  } else {
    const fb = fallbackRate(s, skuEntry);
    const mix = own || fam;
    const now = Object.fromEntries(CHANNELS.map(c =>
      [c.key, mix ? mix.channels[c.server].this_year_per_day : (c.key === 'd2c' ? 1 : 0)]));
    const sum = CHANNELS.reduce((a, c) => a + now[c.key], 0);
    const chShare = c => (sum > 0 ? now[c.key] / sum : (c.key === 'd2c' ? 1 : 0));
    for (const c of CHANNELS) {
      const r = rateOf(c);
      out[c.key] = { today: fb.rate * chShare(c), rate: r.rate, source: r.source, sourceKey: r.source_key };
    }
    basis = `${fb.basis}, split ${CHANNELS.map(c => `${Math.round(chShare(c) * 100)}% ${c.label}`).join(' / ')}`
      + ` by ${own ? "the SKU's" : mix ? "its family's" : 'an assumed'} channel mix`;
  }

  out.today = CHANNELS.reduce((a, c) => a + out[c.key].today, 0);
  out.rate = out.today > 0
    ? CHANNELS.reduce((a, c) => a + out[c.key].today * (1 + out[c.key].rate), 0) / out.today - 1
    : null;
  out.basis = basis;
  return out;
}

// Units/day of total demand `day` days after as_of.
export function demandOnDay(fc, day) {
  return CHANNELS.reduce((a, c) => a + fc[c.key].today * growthMultiplier(fc[c.key].rate, day), 0);
}

// Units over the next 365 days on the forecast curve.
export function next12Months(fc) {
  let total = 0;
  for (let d = 1; d <= 365; d++) total += demandOnDay(fc, d);
  return total;
}
